import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { IntentRing } from "@/components/IntentRing";
import { StatusBadge } from "@/components/StatusBadge";
import { EditionCard } from "@/components/EditionCard";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ArrowLeft, Download, FileDown, Loader2, Play, Pause, Square, ShieldAlert, FlaskConical, BookOpen, Languages,
  Pencil, Sparkles, BadgePercent, CheckCheck, Eye, ListOrdered, SpellCheck, ShieldCheck, Trash2, Plus, Quote, Feather, Users2, Tags, Wand2,
} from "lucide-react";

const PREFACE_MODE_LABEL = {
  mechanical: "Mechanical",
  intro_only: "Intro edited",
  regenerated: "Regenerated",
};
const PREFACE_MODE_CLASS = {
  mechanical: "border-[#e6e4dd] bg-slate-50 text-slate-600",
  intro_only: "border-amber-200 bg-amber-50 text-amber-800",
  regenerated: "border-emerald-200 bg-emerald-50 text-emerald-800",
};

async function downloadFile(url, filename) {
  const { data } = await api.get(url, { responseType: "blob" });
  const href = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = href; a.download = filename; document.body.appendChild(a); a.click();
  a.remove(); URL.revokeObjectURL(href);
}

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [allLanguages, setAllLanguages] = useState([]);
  const [newLang, setNewLang] = useState("");
  const [addingLang, setAddingLang] = useState(false);
  const [addingSourceEd, setAddingSourceEd] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/projects/${id}`);
      setProject(data.project);
    } catch (e) {
      toast.error("Project not found");
      navigate("/dashboard");
    } finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/languages").then(({ data }) => setAllLanguages(data.languages || [])).catch(() => {});
  }, []);

  const processing = project?.editions?.some((e) => ["translating", "verifying", "queued"].includes(e.status));
  useEffect(() => {
    if (!processing) return;
    const t = setInterval(load, 2500);
    return () => clearInterval(t);
  }, [processing, load]);

  if (loading || !project) {
    return (
      <div className="min-h-screen bg-[#f6f5f1]"><Header />
        <div className="flex justify-center py-32"><Loader2 className="h-6 w-6 animate-spin text-[#0f172a]" /></div>
      </div>
    );
  }

  const eds = project.editions;
  const ready = eds.filter((e) => e.epub_ready);
  const scored = eds.filter((e) => e.fidelity_score != null);
  const avg = scored.length ? Math.round(scored.reduce((a, e) => a + e.fidelity_score, 0) / scored.length) : null;
  const totalFlagged = eds.reduce((a, e) => a + (e.flagged_count || 0), 0);
  const anySimulated = eds.some((e) => e.simulated);
  const active = eds.some((e) => ["translating", "verifying", "queued"].includes(e.status));
  const resumable = eds.some((e) => ["pending", "paused", "stopped", "error"].includes(e.status));
  const wasStartedBefore = eds.some((e) => ["paused", "stopped", "error"].includes(e.status));

  const runAll = async () => {
    try { await api.post(`/projects/${id}/run`); toast.success(wasStartedBefore ? "Resuming translation" : "Engine started"); load(); }
    catch (e) { toast.error("Could not start engine"); }
  };

  const pauseAll = async () => {
    try { await api.post(`/projects/${id}/pause`); toast("Pausing at the next safe point…"); load(); }
    catch (e) { toast.error("Could not pause"); }
  };

  const stopAll = async () => {
    try { await api.post(`/projects/${id}/stop`); toast("Stopping translation"); load(); }
    catch (e) { toast.error("Could not stop"); }
  };

  const downloadAll = async () => {
    setDownloadingAll(true);
    try { await downloadFile(`/projects/${id}/download-all`, `${project.title}_catalog.zip`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Nothing ready yet"); }
    finally { setDownloadingAll(false); }
  };

  const addLanguage = async () => {
    if (!newLang) { toast.error("Pick a language first"); return; }
    setAddingLang(true);
    try {
      await api.post(`/projects/${id}/editions`, { lang: newLang });
      toast.success("Language added to the catalog");
      setNewLang("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add language");
    } finally { setAddingLang(false); }
  };

  const hasSourceEdition = eds.some((e) => e.is_source_edition);

  const addSourceEdition = async () => {
    setAddingSourceEd(true);
    try {
      await api.post(`/projects/${id}/source-edition`);
      toast.success("Source manuscript added as an edition");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add source edition");
    } finally { setAddingSourceEd(false); }
  };

  return (
    <div className="min-h-screen bg-[#f6f5f1]">
      <Header />
      <main className="mx-auto max-w-7xl px-5 py-8 sm:px-8">
        <button onClick={() => navigate("/dashboard")} data-testid="back-to-catalog-btn"
          className="inline-flex items-center gap-1.5 text-sm text-[#475569] hover:text-[#0f172a]">
          <ArrowLeft className="h-4 w-4" /> Catalog
        </button>

        {/* Header block */}
        <div className="mt-5 grid grid-cols-1 gap-6 rounded-3xl border border-[#e6e4dd] bg-white p-6 sm:p-8 lg:grid-cols-12">
          <div className="flex gap-5 lg:col-span-8">
            <img src={`${API}/projects/${id}/cover`} alt="cover"
              className="h-40 w-28 flex-shrink-0 rounded-xl object-cover shadow-md" data-testid="project-cover" />
            <div className="min-w-0">
              <h1 className="font-display text-3xl tracking-tight">{project.title}</h1>
              <p className="mt-1 text-[#475569]">by {project.author}</p>
              <div className="mt-4 flex flex-wrap gap-2 text-xs">
                <span className="inline-flex items-center gap-1 rounded-full border border-[#e6e4dd] px-2.5 py-1"><BookOpen className="h-3.5 w-3.5" /> {project.chapter_count} chapters</span>
                <span className="inline-flex items-center gap-1 rounded-full border border-[#e6e4dd] px-2.5 py-1"><Languages className="h-3.5 w-3.5" /> {eds.length} languages</span>
                <span className="inline-flex items-center gap-1 rounded-full border border-[#e6e4dd] px-2.5 py-1 font-mono2">{project.corrector_model}</span>
                {totalFlagged > 0 && <span className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-2.5 py-1 text-rose-700"><ShieldAlert className="h-3.5 w-3.5" /> {totalFlagged} flagged</span>}
              </div>
              {anySimulated && (
                <div className="mt-4 inline-flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-1.5 text-xs text-amber-800" data-testid="simulated-notice">
                  <FlaskConical className="h-3.5 w-3.5" /> SIMULATED run — add API keys in Settings for live RAVIRA translation & verification.
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col items-center justify-center gap-3 border-t border-[#e6e4dd] pt-5 lg:col-span-4 lg:border-l lg:border-t-0 lg:pl-6 lg:pt-0">
            <IntentRing score={avg} size={104} />
            <div className="flex w-full flex-wrap gap-2">
              {active ? (
                <>
                  <Button data-testid="pause-engine-btn" onClick={pauseAll}
                    className="h-10 flex-1 rounded-full bg-[#0f172a] text-white hover:bg-[#1e293b]">
                    <Pause className="mr-1.5 h-4 w-4" /> Pause
                  </Button>
                  <Button data-testid="stop-engine-btn" onClick={stopAll} variant="outline"
                    className="h-10 flex-1 rounded-full border-rose-200 text-rose-600 hover:bg-rose-600 hover:text-white">
                    <Square className="mr-1.5 h-4 w-4" /> Stop
                  </Button>
                </>
              ) : resumable ? (
                <Button data-testid="run-engine-btn" onClick={runAll}
                  className="h-10 flex-1 rounded-full bg-[#0f172a] text-white hover:bg-[#1e293b]">
                  <Play className="mr-1.5 h-4 w-4" /> {wasStartedBefore ? "Resume" : "Run"}
                </Button>
              ) : null}
              <Button data-testid="download-all-btn" onClick={downloadAll} disabled={ready.length === 0 || downloadingAll}
                className="h-10 flex-1 rounded-full bg-[#d97706] text-white hover:bg-[#b45309] disabled:opacity-40">
                {downloadingAll ? <Loader2 className="h-4 w-4 animate-spin" /> : <><FileDown className="mr-1.5 h-4 w-4" /> All (.zip)</>}
              </Button>
            </div>
          </div>
        </div>

        {/* Language cards */}
        <div className="mt-9 flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-display text-2xl tracking-tight">Editions</h2>
          <div className="flex items-center gap-2">
            <Select value={newLang} onValueChange={setNewLang}>
              <SelectTrigger data-testid="add-language-select" className="h-9 w-48 rounded-full border-[#e6e4dd] bg-white text-xs">
                <SelectValue placeholder="Add a language…" />
              </SelectTrigger>
              <SelectContent>
                {allLanguages
                  .filter((l) => !eds.some((e) => e.lang === l.code))
                  .map((l) => (
                    <SelectItem key={l.code} value={l.code}>{l.name} ({l.code.toUpperCase()})</SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <Button data-testid="add-language-btn" size="sm" onClick={addLanguage} disabled={!newLang || addingLang}
              className="h-9 rounded-full bg-[#0f172a] text-white hover:bg-[#1e293b] disabled:opacity-40">
              {addingLang ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="mr-1 h-4 w-4" /> Add</>}
            </Button>
            {!hasSourceEdition && (
              <Button data-testid="add-source-edition-btn" size="sm" variant="outline" onClick={addSourceEdition}
                disabled={addingSourceEd}
                title="Package the manuscript's own language as an EPUB edition, with no translation and no corrector passes"
                className="h-9 rounded-full border-[#e6e4dd] bg-white text-xs hover:bg-slate-900 hover:text-white disabled:opacity-40">
                {addingSourceEd ? <Loader2 className="h-4 w-4 animate-spin" /> : <><BookOpen className="mr-1.5 h-4 w-4" /> Add original (no translation)</>}
              </Button>
            )}
          </div>
        </div>
        <div className="mt-4 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
          {eds.map((e, i) => (
            <EditionCard key={e.id} projectId={id} projectTitle={project.title} e={e} index={i} onChanged={load} />
          ))}
        </div>
      </main>
    </div>
  );
}
